import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAllContacts, 
  updateContactStatus, 
  deleteContact, 
  getStats,
  adminLogout 
} from '../services/api';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    search: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [filters]);

  const fetchContacts = async () => {
    setIsLoading(true);
    const result = await getAllContacts(filters);
    
    if (result.success) {
      setContacts(result.data);
      setPagination(result.pagination);
    }
    
    setIsLoading(false);
  };

  const fetchStats = async () => {
    const result = await getStats();
    if (result.success) {
      setStats(result.data);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
      page: 1 // Reset à la page 1 lors du changement de filtre
    });
  };

  const handleStatusChange = async (id, newStatus) => {
    const result = await updateContactStatus(id, newStatus);
    
    if (result.success) {
      fetchContacts();
      fetchStats();
      if (selectedContact && selectedContact._id === id) {
        setSelectedContact(result.data);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      const result = await deleteContact(id);
      
      if (result.success) {
        fetchContacts();
        fetchStats();
        setShowModal(false);
        setSelectedContact(null);
      }
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const openModal = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedContact(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      nouveau: '#3b82f6',
      en_cours: '#f59e0b',
      traite: '#22c55e',
      archive: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      nouveau: 'Nouveau',
      en_cours: 'En cours',
      traite: 'Traité',
      archive: 'Archivé'
    };
    return labels[status] || status;
  };

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>📊 Dashboard Admin</h1>
          <p>Bienvenue, {adminUser.name}</p>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate('/admin/payments')} className="payments-button">
            💳 Paiements
          </button>
          <button onClick={handleLogout} className="logout-button">
            🚪 Déconnexion
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📬</div>
            <div className="stat-content">
              <p className="stat-label">Total demandes</p>
              <p className="stat-value">{stats.total}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">💼</div>
            <div className="stat-content">
              <p className="stat-label">Clients</p>
              <p className="stat-value">{stats.byType.client}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🏢</div>
            <div className="stat-content">
              <p className="stat-label">Entreprises</p>
              <p className="stat-value">{stats.byType.entreprise}</p>
            </div>
          </div>

          <div className="stat-card highlight">
            <div className="stat-icon">🔔</div>
            <div className="stat-content">
              <p className="stat-label">Nouveaux</p>
              <p className="stat-value">{stats.byStatus.nouveau}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <select 
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="filter-select"
          >
            <option value="">Tous les types</option>
            <option value="client">Clients</option>
            <option value="entreprise">Entreprises</option>
          </select>

          <select 
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">Tous les statuts</option>
            <option value="nouveau">Nouveau</option>
            <option value="en_cours">En cours</option>
            <option value="traite">Traité</option>
            <option value="archive">Archivé</option>
          </select>

          <input
            type="text"
            placeholder="🔍 Rechercher par nom ou email..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Contacts Table */}
      <div className="contacts-section">
        <h2>Liste des demandes</h2>
        
        {isLoading ? (
          <div className="loading">Chargement...</div>
        ) : contacts.length === 0 ? (
          <div className="empty-state">
            <p>Aucune demande trouvée</p>
          </div>
        ) : (
          <>
            <div className="contacts-table">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Statut</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact._id}>
                      <td>
                        <span className={`type-badge ${contact.type}`}>
                          {contact.type === 'client' ? '💼' : '🏢'} {contact.type}
                        </span>
                      </td>
                      <td className="name-cell">
                        {contact.type === 'client' ? contact.nom : contact.nomRecruteur}
                      </td>
                      <td>{contact.email}</td>
                      <td>
                        <select
                          value={contact.status}
                          onChange={(e) => handleStatusChange(contact._id, e.target.value)}
                          className="status-select"
                          style={{ color: getStatusColor(contact.status) }}
                        >
                          <option value="nouveau">Nouveau</option>
                          <option value="en_cours">En cours</option>
                          <option value="traite">Traité</option>
                          <option value="archive">Archivé</option>
                        </select>
                      </td>
                      <td>{new Date(contact.createdAt).toLocaleDateString('fr-FR')}</td>
                      <td className="actions-cell">
                        <button 
                          onClick={() => openModal(contact)}
                          className="action-btn view-btn"
                          title="Voir les détails"
                        >
                          👁️
                        </button>
                        <button 
                          onClick={() => handleDelete(contact._id)}
                          className="action-btn delete-btn"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handleFilterChange('page', filters.page - 1)}
                  disabled={filters.page === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                
                <span className="pagination-info">
                  Page {pagination.page} sur {pagination.pages}
                </span>
                
                <button
                  onClick={() => handleFilterChange('page', filters.page + 1)}
                  disabled={filters.page === pagination.pages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal détails */}
      {showModal && selectedContact && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Détails de la demande</h2>
              <button onClick={closeModal} className="close-btn">✕</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>
                  {selectedContact.type === 'client' ? '💼 Informations client' : '🏢 Informations entreprise'}
                </h3>
                {selectedContact.type === 'client' ? (
                  <>
                    <p><strong>Nom :</strong> {selectedContact.nom}</p>
                    <p><strong>Email :</strong> {selectedContact.email}</p>
                    {selectedContact.telephone && (
                      <p><strong>Téléphone :</strong> {selectedContact.telephone}</p>
                    )}
                    <p><strong>Type de projet :</strong> {selectedContact.typeProjet}</p>
                    <p><strong>Budget :</strong> {selectedContact.budget}</p>
                    <p><strong>Délai :</strong> {selectedContact.delai}</p>
                  </>
                ) : (
                  <>
                    <p><strong>Entreprise :</strong> {selectedContact.entreprise}</p>
                    <p><strong>Recruteur :</strong> {selectedContact.nomRecruteur}</p>
                    <p><strong>Email :</strong> {selectedContact.email}</p>
                    {selectedContact.telephone && (
                      <p><strong>Téléphone :</strong> {selectedContact.telephone}</p>
                    )}
                    <p><strong>Poste :</strong> {selectedContact.poste}</p>
                    <p><strong>Type de contrat :</strong> {selectedContact.typeContrat}</p>
                    {selectedContact.localisation && (
                      <p><strong>Localisation :</strong> {selectedContact.localisation}</p>
                    )}
                    <p><strong>Remote :</strong> {selectedContact.remote}</p>
                    {selectedContact.salaire && (
                      <p><strong>Salaire :</strong> {selectedContact.salaire}</p>
                    )}
                  </>
                )}
              </div>

              <div className="detail-section">
                <h3>📝 Description</h3>
                <p className="description-text">{selectedContact.description}</p>
              </div>

              <div className="detail-section">
                <h3>📊 Statut actuel</h3>
                <select
                  value={selectedContact.status}
                  onChange={(e) => handleStatusChange(selectedContact._id, e.target.value)}
                  className="status-select-modal"
                  style={{ color: getStatusColor(selectedContact.status) }}
                >
                  <option value="nouveau">Nouveau</option>
                  <option value="en_cours">En cours</option>
                  <option value="traite">Traité</option>
                  <option value="archive">Archivé</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => handleDelete(selectedContact._id)} className="delete-modal-btn">
                🗑️ Supprimer
              </button>
              <button onClick={closeModal} className="close-modal-btn">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;